import { useEffect, useState } from 'react'
import { Search, Download, Users, BookOpen, Award, Mail, MoreHorizontal } from 'lucide-react'

interface Student {
  _id: string
  name: string
  email: string
  phone?: string
  enrolledCourses: number
  completedCourses: number
  totalSpent: number
  joinedDate: string
  lastActive: string
  avatar?: string
  isActive: boolean
}

export default function AdminStudents() {
  const [students, setStudents] = useState<Student[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    fetchStudents()
  }, [])

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/users?role=USER', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        // Map the backend user data to match our Student interface
        const mappedStudents = (data.users || []).map((user: any) => ({
          _id: user._id,
          name: user.name || 'Unknown',
          email: user.email || '',
          phone: user.phone || '',
          enrolledCourses: user.stats?.enrollments || 0,
          completedCourses: user.stats?.completed || 0,
          totalSpent: 0, // Will be calculated from payments
          joinedDate: user.createdAt || new Date().toISOString(),
          lastActive: user.updatedAt || new Date().toISOString(),
          avatar: user.avatar || '',
          isActive: true
        }))
        setStudents(mappedStudents)
      } else {
        console.error('Failed to fetch students:', response.status)
        setStudents([])
      }
    } catch (error) {
      console.error('Error fetching students:', error)
      setStudents([])
    } finally {
      setLoading(false)
    }
  }

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Enrolled Courses', 'Total Spent', 'Joined Date']
    const csvData = students.map(s => [
      s.name,
      s.email,
      s.phone || '',
      s.enrolledCourses,
      s.totalSpent,
      new Date(s.joinedDate).toLocaleDateString()
    ])

    const csv = [headers, ...csvData].map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `students-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
  }

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const stats = {
    total: students.length,
    active: students.filter(s => s.isActive).length,
    totalRevenue: students.reduce((sum, s) => sum + (s.totalSpent || 0), 0),
    avgCoursesPerStudent: students.length > 0
      ? (students.reduce((sum, s) => sum + s.enrolledCourses, 0) / students.length).toFixed(1)
      : 0
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.total}</p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Total Students</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl">
              <Award className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.active}</p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Active Students</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-violet-50 dark:bg-violet-900/20 rounded-2xl">
              <BookOpen className="h-6 w-6 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.avgCoursesPerStudent}</p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Avg Courses/Student</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-rose-50 dark:bg-rose-900/20 rounded-2xl">
              <Mail className="h-6 w-6 text-rose-600 dark:text-rose-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">₹{stats.totalRevenue.toLocaleString()}</p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Total Revenue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white dark:bg-dark-800 rounded-3xl p-4 shadow-sm border border-gray-100 dark:border-dark-700">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400" />
            <input
              type="text"
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
            />
          </div>
          <button
            onClick={exportCSV}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20 hover:shadow-xl hover:shadow-rose-600/30"
          >
            <Download className="h-5 w-5" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white dark:bg-dark-800 rounded-3xl border border-gray-100 dark:border-dark-700 overflow-hidden shadow-sm">
        {filteredStudents.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gray-50 dark:bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="h-10 w-10 text-charcoal-400" />
            </div>
            <h3 className="text-xl font-bold text-charcoal-900 dark:text-white mb-2">
              No students found
            </h3>
            <p className="text-charcoal-500 dark:text-charcoal-400 text-sm">
              {searchQuery ? 'Try adjusting your search query' : 'Students will appear here once they register'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-900/20">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Student</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Enrolled</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Spent</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                {filteredStudents.map((student) => (
                  <tr key={student._id} className="hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-100 to-rose-200 dark:from-rose-900/30 dark:to-rose-800/30 flex items-center justify-center text-rose-600 dark:text-rose-400 font-bold text-sm">
                          {student.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-charcoal-900 dark:text-white">{student.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <p className="text-sm text-charcoal-600 dark:text-charcoal-300">{student.email}</p>
                        {student.phone && (
                          <p className="text-xs text-charcoal-400 dark:text-charcoal-500">{student.phone}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                        {student.enrolledCourses} Courses
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                        ₹{(student.totalSpent || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-charcoal-500 dark:text-charcoal-400">
                        {new Date(student.joinedDate).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${student.isActive
                          ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${student.isActive ? 'bg-green-500' : 'bg-gray-500'
                          }`}></span>
                        {student.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="p-2 text-charcoal-400 hover:text-charcoal-600 dark:hover:text-charcoal-200 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors">
                        <MoreHorizontal size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
